import { useMemo } from 'react';
import { generateArticleStructuredData, SITE_CONFIG } from '../utils/seoUtils';

/**
 * Hook personnalisé pour générer les données SEO d'un article
 * @param {Object} article - L'article avec ses données
 * @returns {Object} Données SEO formatées pour l'article
 */
export const useArticleSEO = (article) => {
  return useMemo(() => {
    if (!article) {
      return {
        title: 'Article non trouvé - Edutio',
        description: 'Cet article n\'existe pas ou n\'est plus disponible.',
        keywords: 'article, formation professionnelle, CFA',
        canonical: `${SITE_CONFIG.baseUrl}/actualites`,
        structuredData: null,
        noindex: true
      };
    }

    const category = article.expand?.category;
    const articleUrl = `${SITE_CONFIG.baseUrl}/article/${article.slug || article.id}`;
    
    // Générer le titre optimisé pour le SEO avec mots-clés stratégiques
    const categoryKeyword = category?.name?.toLowerCase() || 'formation';
    const seoTitle = article.title.length > 60 
      ? `${article.title.substring(0, 50)}... | ${categoryKeyword} | Edutio`
      : `${article.title} | ${categoryKeyword} professionnelle | Edutio`;
    
    // Générer la description optimisée avec call-to-action
    let seoDescription = article.excerpt || '';
    if (!seoDescription && article.content) {
      seoDescription = article.content.replace(/<[^>]*>/g, '').substring(0, 140);
    }
    if (!seoDescription) {
      seoDescription = `Découvrez notre guide complet sur ${article.title.toLowerCase()}`;
    }
    // Ajouter un CTA si la description est courte
    if (seoDescription.length < 140) {
      seoDescription += ` ✓ Conseils d'experts ✓ Solutions CFA ✓ Guide pratique`;
    }
    seoDescription = seoDescription.substring(0, 155) + (seoDescription.length > 155 ? '...' : '');
    
    // Générer les mots-clés longue traîne optimisés
    const titleWords = article.title.toLowerCase().split(' ').filter(word => word.length > 3);
    const keywords = [
      ...titleWords.slice(0, 3),
      'formation professionnelle',
      'CFA',
      'apprentissage',
      'gestion formation',
      category?.name?.toLowerCase() || 'actualités',
      'logiciel CFA',
      'digitalisation formation'
    ].join(', ');
    
    // URL de l'image
    const imageUrl = article.cover_image 
      ? `${SITE_CONFIG.baseUrl}/api/files/articles/${article.id}/${article.cover_image}`
      : `${SITE_CONFIG.baseUrl}${SITE_CONFIG.defaultImage}`;
    
    return {
      title: seoTitle,
      description: seoDescription,
      keywords: keywords,
      canonical: articleUrl,
      image: imageUrl,
      structuredData: generateArticleStructuredData(article, category),
      noindex: false,
      article: article // Pour les meta tags spécifiques aux articles
    };
  }, [article]);
};
